import { lazy, Suspense, useMemo } from 'react'
import Chart from 'react-apexcharts'
import type { CardChart, ChartCard } from '@/features/dashboard/api/schemas'
import { chartSummary } from './lib/chartSummary'
import {
  deepDiveKind,
  deepDiveModel,
  deepDiveOptions,
  deepDiveSeries,
} from './lib/deepDiveOptions'
import { useChartPalette } from './lib/useChartPalette'
import styles from './DeepDiveChart.module.css'

// The map's outlines are a sizeable chunk of data: loaded when a map is drawn.
const ChoroplethMap = lazy(() =>
  import('./ChoroplethMap').then(module => ({ default: module.ChoroplethMap }))
)

type DeepDiveChartProps = {
  card: ChartCard
  /** How the deep dive draws the card (from its stub); the card's own type if absent. */
  chart?: CardChart | null | undefined
  height?: number
}

/**
 * A deep-dive chart: bars with their
 * values above, or lines and areas (stacked, with totals, if the deep dive says
 * so), benchmark series as dashed lines, and a map of European markets for
 * `map`. Every other type is drawn as an area.
 */
export const DeepDiveChart = ({
  card,
  chart,
  height = 340,
}: DeepDiveChartProps) => {
  const { theme, palette } = useChartPalette()
  const isMap = deepDiveKind(card, chart) === 'map'

  const model = useMemo(() => deepDiveModel(card, chart), [card, chart])
  const options = useMemo(
    () => deepDiveOptions(card, model, palette, theme, height),
    [card, model, palette, theme, height]
  )
  const series = useMemo(() => deepDiveSeries(model), [model])

  return (
    <div role='img' aria-label={chartSummary(card)} className={styles.chart}>
      {isMap ? (
        <Suspense fallback={<div className={styles.loading} />}>
          <ChoroplethMap card={card} />
        </Suspense>
      ) : (
        <Chart
          type={model.apexType}
          height={height}
          width='100%'
          options={options}
          series={series}
        />
      )}
    </div>
  )
}
